import orderLogQueries from '@apis/queries/orderLogQueries';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import RefreshIcon from '@icons/Refresh';
import { OrderStatus } from '@models/order/orderLog';
import { OrderStep } from '@models/order/orderLog/extension';
import { pressActionEffect } from '@styles/mixin';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Dot from './Dot';

interface Props {
    orderStatus: OrderStatus
}

const STEP_LABEL_KEYS = [
    'progress.paymentCompleted',
    'progress.orderConfirmed',
    'progress.preparationCompleted',
    'progress.pickupCompleted',
] as const;

function OrderProgress({ orderStatus }:Props) {
    const { t } = useTranslation('orderLog');
    const step = OrderStep[orderStatus];
    const progressWidth = (step / 3) * 100;
    const queryClient = useQueryClient();
    const [refreshing, setRefreshing] = useState(false);
    const handleRefresh = () => {
        queryClient.invalidateQueries({
            queryKey: orderLogQueries._def,
        });
        setRefreshing(true);
        const timeout = setTimeout(() => {
            setRefreshing(false);
        }, 201);
        return () => clearTimeout(timeout);
    };
    if (orderStatus === 'CANCELLED') {
        return null;
    }
    return (
        <Container fullWidth align='center' spacing={4}>
            <Column fullWidth>
                <ProgressWrapper>
                    <Progress>
                        <ProgressBar style={{ width: `calc(${progressWidth}% + 40px)` }}/>
                        <Dot isActive={step === 0}/>
                        <Dot isActive={step === 1}/>
                        <Dot isActive={step === 2}/>
                        <Dot isActive={step === 3}/>
                    </Progress>
                </ProgressWrapper>
                <Texts>
                    {STEP_LABEL_KEYS.map((key, index) => (
                        <Text key={key}
                            fontWeight='medium'
                            align='center'
                            color={index <= step
                                ? 'var(--color-text-primary)'
                                : 'var(--color-text-unseledcted)'} >
                            {t(key)}
                        </Text>
                    ))}
                </Texts>
            </Column>
            {orderStatus !== 'DONE' && (
                <UpdateButton spacing={2}
                    id='refresh-order-button'
                    justify='center'
                    onClick={handleRefresh}
                    css={pressActionEffect} >
                    <RefreshIcon size={20}
                        color={colors.gray7}
                        css={refreshing && refreshAnimation} />
                    <Text fontWeight='bold' color='var(--color-text-secondary)'>
                        {t('info.updateStatus')}
                    </Text>
                </UpdateButton>
            )}
        </Container>
    );
}

const Texts = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    width: 100%;
`;

const Container = styled(Column)`
    padding: 4px 0 0;
`;

const ProgressWrapper = styled.div`
    width: 100%;
    padding: 0 16px;
`;

const progressShape = css`
    border-radius: 20px;
    height: 14px;
`;

const Progress = styled.div`
    ${progressShape}
    align-items: center;
    background-color: var(--color-background-raised);
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--spacing-2);
    position: relative;
    width: 100%;
`;

const ProgressBar = styled.div`
    ${progressShape}
    background-color: var(--color-primary);
    max-width: 100%;
    left: 0;
    position: absolute;
    top: 0;
    transition: 200ms ease-in-out;
`;

const refreshAnimation = css`
    transform: rotate(360deg);
    transition: 200ms ease-in-out;
`;

const UpdateButton = styled(Row)`
    background-color: transparent;
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-lg);
    cursor: pointer;
    height: 50px;
    min-width: 100px;
    padding: 0 var(--spacing-5);
    width: fit-content;
`;

export default OrderProgress;
